import { useEffect, useMemo, useState } from "react";

import { PalAutoComplete, PalTextField } from "@palamar/fe-library";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import { Grid } from "@mui/material";

import useProjectChange from "~common/hooks/useProjectChange";
import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRPage from "~components/Generic/PRPage";
import { invoiceTypesOptions } from "~constants";
import DateHelper from "~helpers/DateHelper";
import HistoryHelper from "~helpers/HistoryHelper";
import {
  createInvoice,
  getBillList,
  getBillSettingList,
  getInvoiceSettingsList,
} from "~store/billingAndInvoicing/actions";
import { selectBillList, selectBillSettingList, selectInvoiceSettingsList } from "~store/billingAndInvoicing/selectors";
import { selectCurrentProject } from "~store/user/selectors";

const InvoicesEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams(); // Although not used for editing, it's kept for consistency with route structure
  const dispatch = useDispatch();
  const currentProject = useSelector(selectCurrentProject);
  const billList = useSelector(selectBillList);
  const billSettingList = useSelector(selectBillSettingList);
  const invoiceSettingsList = useSelector(selectInvoiceSettingsList);

  useEffect(() => {
    dispatch(getBillList(currentProject.id));
    dispatch(getInvoiceSettingsList(currentProject.id));
    dispatch(getBillSettingList(currentProject.id));
  }, [dispatch, currentProject.id]);

  const [initialValues, setInitialValues] = useState({
    bill: null,
    invoice_settings: null,
    invoice_receiver_inbox_tag: "",
    invoice_type: null,
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      bill: Yup.object().required(t("common.required")).nullable(),
      invoice_settings: Yup.object().required(t("common.required")).nullable(),
      invoice_receiver_inbox_tag: Yup.string().required(t("common.required")),
      invoice_type: Yup.string().required(t("common.required")).nullable(),
    }),
    onSubmit: async (values) => {
      const payload = {
        bill: values.bill.id,
        invoice_settings: values.invoice_settings.id,
        invoice_receiver_inbox_tag: values.invoice_receiver_inbox_tag,
        invoice_type: values.invoice_type.value,
      };
      await dispatch(createInvoice(currentProject.id, payload));
      handleClickCancel();
    },
  });

  useProjectChange(() => {
    HistoryHelper.push("/billing-and-invoicing/invoices", {
      scope: "dashboard",
    });
  }, []);

  const parentName = useMemo(
    () => [
      {
        label: t("layout.sidebar.invoices"),
        url: "/billing-and-invoicing/invoices",
      },
      {
        label: t("dashboard.invoicesEdit.createInvoice"),
      },
    ],
    [t]
  );

  // const selectedBill = useMemo(() => {
  //   const selectedBill = billList.find((item) => item.id === formik.values.bill?.id);
  //   return selectedBill || null;
  // }, [billList, formik.values.bill]);

  // const billingSettingsItemForSelectedBill = useMemo(() => {
  //   if (!selectedBill) return null;
  //   return billSettingList.find((item) => item.bill.id === selectedBill.billing_settings) || null;
  // }, [billSettingList, selectedBill]);

  const billSelectRenderer = (option) => {
    const relatedBillSettingSubItem = billSettingList?.find((item) => item.id === option.billing_settings);
    const currencyCode = relatedBillSettingSubItem?.currency_code || "";
    const dueDate = DateHelper.getDateTimeLocal(option.due_date).format("L");
    let totalPrice = option.items.reduce(
      (total, item) => total + (Number(item.base_amount) + Number(item.vat_amount)),
      0
    );

    return `Due ${dueDate} - ${currencyCode} ${totalPrice.toFixed(2)}`;
  };
  const invoiceSettingsRenderer = (option) => {
    return `${option.receiver_name} - ${option.receiver_tax_code} - ${option.receiver_address} - ${option.receiver_city} - ${option.receiver_town} - ${option.receiver_postal_code}`;
  };
  const handleClickCancel = () => {
    HistoryHelper.goBack("/billing-and-invoicing/invoices/", { scope: "dashboard" });
  };

  return (
    <PRContainer name={t("layout.sidebar.billingAndInvoicing")} parentName={parentName}>
      <PRPage>
        <Grid
          container
          spacing={{
            xs: 1,
            md: 2,
          }}
        >
          <Grid item md={6} xs={12}>
            <PalAutoComplete
              fullWidth
              invalid={formik.touched.bill && formik.errors.bill}
              label={t("dashboard.invoices.bill")}
              labelRenderer={billSelectRenderer}
              labelSelector="due_date"
              options={billList}
              value={formik.values.bill}
              valueSelector="id"
              onBlur={() => formik.setFieldTouched("bill", true)}
              onChange={(event, newValue) => formik.setFieldValue("bill", newValue)}
            />
          </Grid>

          <Grid item md={6} xs={12}></Grid>
          <Grid item md={6} xs={12}>
            <PalAutoComplete
              fullWidth
              invalid={formik.touched.invoice_settings && formik.errors.invoice_settings}
              label={t("dashboard.invoices.invoiceSettings")}
              labelRenderer={invoiceSettingsRenderer}
              options={invoiceSettingsList}
              value={formik.values.invoice_settings}
              onBlur={() => formik.setFieldTouched("invoice_settings", true)}
              onChange={(event, newValue) => formik.setFieldValue("invoice_settings", newValue)}
            />
          </Grid>
          <Grid item md={6} xs={12}></Grid>
          <Grid item md={6} xs={12}>
            <PalTextField
              fullWidth
              invalid={formik.touched.invoice_receiver_inbox_tag && formik.errors.invoice_receiver_inbox_tag}
              label={t("dashboard.invoices.invoiceReceiverInboxTag")}
              name="invoice_receiver_inbox_tag"
              value={formik.values.invoice_receiver_inbox_tag}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6} xs={12}></Grid>
          <Grid item md={6} xs={12}>
            <PalAutoComplete
              fullWidth
              invalid={formik.touched.invoice_type && formik.errors.invoice_type}
              label={t("dashboard.invoices.invoiceType")}
              options={invoiceTypesOptions}
              value={formik.values.invoice_type}
              onBlur={() => formik.setFieldTouched("invoice_type", true)}
              onChange={(event, newValue) => formik.setFieldValue("invoice_type", newValue)}
            />
          </Grid>
          <Grid item md={6} xs={12}></Grid>
        </Grid>
        <Grid container mt={2}>
          <Grid item ml="auto">
            <PRButton outline className="me-2" onClick={handleClickCancel}>
              {t("common.cancel")}
            </PRButton>
            <PRButton onClick={formik.handleSubmit}>{t("common.create")}</PRButton>
          </Grid>
        </Grid>
      </PRPage>
    </PRContainer>
  );
};

export default InvoicesEdit;
